import { LocationData } from '../services/DashboardService';

export interface AnomalyFlag {
  location: LocationData;
  reason: string;
  severity: 'warning' | 'critical';
  details: {
    sales: number;
    gfsPercent: number;
    gfsSpending: number;
    comparisonLocation: {
      name: string;
      sales: number;
      gfsPercent: number;
      gfsSpending: number;
    };
    salesDifference: number;
    gfsPercentDifference: number;
  };
}

export interface AnomalyDetectionConfig {
  // Sales similarity thresholds (how close sales must be to compare)
  salesSimilarityThreshold: number; // e.g., 1000 - locations within $1000 of each other
  strictSalesSimilarityThreshold: number; // e.g., 100 - stricter threshold within $100
  
  // GFS% difference thresholds (percentage points)
  gfsDifferenceThreshold: number; // e.g., 2 percentage points
  strictGfsDifferenceThreshold: number; // e.g., 1 percentage point for stricter matching
  
  // Minimum number of similar locations to compare against
  minSimilarLocations: number; // e.g., 2
}

export const DEFAULT_ANOMALY_CONFIG: AnomalyDetectionConfig = {
  salesSimilarityThreshold: 1000,
  strictSalesSimilarityThreshold: 100,
  gfsDifferenceThreshold: 2,
  strictGfsDifferenceThreshold: 1,
  minSimilarLocations: 2,
};

/**
 * Finds locations with similar 8th week sales (within threshold) for comparison
 */
function findSimilarLocations(
  location: LocationData,
  allLocations: LocationData[],
  salesDifferenceThreshold: number,
  minSimilarLocations: number
): LocationData[] {
  const similar: LocationData[] = [];
  const locationSales = location.eightWeekTotalSales ?? 0;
  
  for (const other of allLocations) {
    if (other.location === location.location) {
      continue; // Skip self
    }
    
    const otherSales = other.eightWeekTotalSales ?? 0;
    const salesDiff = Math.abs(locationSales - otherSales);
    
    // If 8th week sales are within threshold, consider them similar
    if (salesDiff <= salesDifferenceThreshold) {
      similar.push(other);
    }
  }
  
  return similar.length >= minSimilarLocations - 1 ? similar : [];
}

/**
 * Detects anomalies: locations with similar sales but significantly different GFS spending patterns
 * Compares locations with sales within $1000 (or $100) of each other
 * Flags if GFS% differs by more than 2 (or 1) percentage points
 */
export function detectAnomalies(
  locations: LocationData[],
  config: Partial<AnomalyDetectionConfig> = {}
): AnomalyFlag[] {
  const finalConfig = { ...DEFAULT_ANOMALY_CONFIG, ...config };
  const flags: AnomalyFlag[] = [];
  const flaggedPairs = new Set<string>(); // Track flagged location pairs to avoid duplicates
  
  // Only check locations with valid 8th week GFS data
  const validLocations = locations.filter(loc => {
    const eightWeekSales = loc.eightWeekTotalSales ?? 0;
    const eightWeekGfs = loc.eightWeekTotalGfs ?? 0;
    const eightWeekGfsPercent = loc.eightWeekGfsPercentValue ?? 
      (eightWeekSales > 0 && eightWeekGfs > 0 ? (eightWeekGfs / eightWeekSales) * 100 : 0);
    
    return eightWeekSales > 0 && eightWeekGfsPercent > 0 && !isNaN(eightWeekGfsPercent) && isFinite(eightWeekGfsPercent);
  });
  
  for (const location of validLocations) {
    // Get 8th week data for current location
    const locationEightWeekSales = location.eightWeekTotalSales ?? 0;
    const locationEightWeekGfs = location.eightWeekTotalGfs ?? 0;
    const locationEightWeekGfsPercent = location.eightWeekGfsPercentValue ?? 
      (locationEightWeekSales > 0 ? (locationEightWeekGfs / locationEightWeekSales) * 100 : 0);
    
    // First try strict threshold ($100 difference in 8th week sales)
    const strictSimilar = findSimilarLocations(
      location,
      validLocations,
      finalConfig.strictSalesSimilarityThreshold,
      finalConfig.minSimilarLocations
    );
    
    if (strictSimilar.length > 0) {
      // Found locations within $100 - check if 8th week GFS% differs by more than 1%
      // Find all that meet the threshold
      const qualifyingComparisons = strictSimilar.filter(similar => {
        const similarEightWeekSales = similar.eightWeekTotalSales ?? 0;
        const similarEightWeekGfs = similar.eightWeekTotalGfs ?? 0;
        const similarEightWeekGfsPercent = similar.eightWeekGfsPercentValue ?? 
          (similarEightWeekSales > 0 ? (similarEightWeekGfs / similarEightWeekSales) * 100 : 0);
        
        const gfsDiff = Math.abs(locationEightWeekGfsPercent - similarEightWeekGfsPercent);
        return gfsDiff > finalConfig.strictGfsDifferenceThreshold;
      });
      
      if (qualifyingComparisons.length > 0) {
        // Find the best performer (lowest 8th week GFS%) among qualifying comparisons
        const bestPerformer = qualifyingComparisons.reduce((best, current) => {
          const bestSales = best.eightWeekTotalSales ?? 0;
          const bestGfs = best.eightWeekTotalGfs ?? 0;
          const bestPercent = best.eightWeekGfsPercentValue ?? (bestSales > 0 ? (bestGfs / bestSales) * 100 : 0);
          
          const currentSales = current.eightWeekTotalSales ?? 0;
          const currentGfs = current.eightWeekTotalGfs ?? 0;
          const currentPercent = current.eightWeekGfsPercentValue ?? (currentSales > 0 ? (currentGfs / currentSales) * 100 : 0);
          
          return currentPercent < bestPercent ? current : best;
        });
        
        const bestEightWeekSales = bestPerformer.eightWeekTotalSales ?? 0;
        const bestEightWeekGfs = bestPerformer.eightWeekTotalGfs ?? 0;
        const bestEightWeekGfsPercent = bestPerformer.eightWeekGfsPercentValue ?? 
          (bestEightWeekSales > 0 ? (bestEightWeekGfs / bestEightWeekSales) * 100 : 0);
        
        // Only flag if current location has HIGHER 8th week GFS% than best performer
        if (locationEightWeekGfsPercent > bestEightWeekGfsPercent) {
          const pairKey = [location.location, bestPerformer.location].sort().join('|');
          
          if (!flaggedPairs.has(pairKey)) {
            flaggedPairs.add(pairKey);
            
            const gfsDiff = locationEightWeekGfsPercent - bestEightWeekGfsPercent;
            const salesDiff = Math.abs(locationEightWeekSales - bestEightWeekSales);
            
            flags.push({
              location: location,
              reason: `8th week sales within $${salesDiff.toFixed(0)} of ${bestPerformer.location}, but GFS% is ${gfsDiff.toFixed(1)} points higher`,
              severity: 'critical',
              details: {
                sales: locationEightWeekSales,
                gfsPercent: locationEightWeekGfsPercent,
                gfsSpending: locationEightWeekGfs,
                comparisonLocation: {
                  name: bestPerformer.location,
                  sales: bestEightWeekSales,
                  gfsPercent: bestEightWeekGfsPercent,
                  gfsSpending: bestEightWeekGfs,
                },
                salesDifference: salesDiff,
                gfsPercentDifference: gfsDiff,
              },
            });
          }
        }
      }
    } else {
      // Try regular threshold ($1000 difference in 8th week sales)
      const regularSimilar = findSimilarLocations(
        location,
        validLocations,
        finalConfig.salesSimilarityThreshold,
        finalConfig.minSimilarLocations
      );
      
      if (regularSimilar.length > 0) {
        // Found locations within $1000 - check if 8th week GFS% differs by more than 2%
        // Find all that meet the threshold
        const qualifyingComparisons = regularSimilar.filter(similar => {
          const similarEightWeekSales = similar.eightWeekTotalSales ?? 0;
          const similarEightWeekGfs = similar.eightWeekTotalGfs ?? 0;
          const similarEightWeekGfsPercent = similar.eightWeekGfsPercentValue ?? 
            (similarEightWeekSales > 0 ? (similarEightWeekGfs / similarEightWeekSales) * 100 : 0);
          
          const gfsDiff = Math.abs(locationEightWeekGfsPercent - similarEightWeekGfsPercent);
          return gfsDiff > finalConfig.gfsDifferenceThreshold;
        });
        
        if (qualifyingComparisons.length > 0) {
          // Find the best performer (lowest 8th week GFS%) among qualifying comparisons
          const bestPerformer = qualifyingComparisons.reduce((best, current) => {
            const bestSales = best.eightWeekTotalSales ?? 0;
            const bestGfs = best.eightWeekTotalGfs ?? 0;
            const bestPercent = best.eightWeekGfsPercentValue ?? (bestSales > 0 ? (bestGfs / bestSales) * 100 : 0);
            
            const currentSales = current.eightWeekTotalSales ?? 0;
            const currentGfs = current.eightWeekTotalGfs ?? 0;
            const currentPercent = current.eightWeekGfsPercentValue ?? (currentSales > 0 ? (currentGfs / currentSales) * 100 : 0);
            
            return currentPercent < bestPercent ? current : best;
          });
          
          const bestEightWeekSales = bestPerformer.eightWeekTotalSales ?? 0;
          const bestEightWeekGfs = bestPerformer.eightWeekTotalGfs ?? 0;
          const bestEightWeekGfsPercent = bestPerformer.eightWeekGfsPercentValue ?? 
            (bestEightWeekSales > 0 ? (bestEightWeekGfs / bestEightWeekSales) * 100 : 0);
          
          // Only flag if current location has HIGHER 8th week GFS% than best performer
          if (locationEightWeekGfsPercent > bestEightWeekGfsPercent) {
            const pairKey = [location.location, bestPerformer.location].sort().join('|');
            
            if (!flaggedPairs.has(pairKey)) {
              flaggedPairs.add(pairKey);
              
              const gfsDiff = locationEightWeekGfsPercent - bestEightWeekGfsPercent;
              const salesDiff = Math.abs(locationEightWeekSales - bestEightWeekSales);
              
              flags.push({
                location: location,
                reason: `8th week sales within $${salesDiff.toFixed(0)} of ${bestPerformer.location}, but GFS% is ${gfsDiff.toFixed(1)} points higher`,
                severity: 'warning',
                details: {
                  sales: locationEightWeekSales,
                  gfsPercent: locationEightWeekGfsPercent,
                  gfsSpending: locationEightWeekGfs,
                  comparisonLocation: {
                    name: bestPerformer.location,
                    sales: bestEightWeekSales,
                    gfsPercent: bestEightWeekGfsPercent,
                    gfsSpending: bestEightWeekGfs,
                  },
                  salesDifference: salesDiff,
                  gfsPercentDifference: gfsDiff,
                },
              });
            }
          }
        }
      }
    }
  }
  
  return flags;
}

/**
 * Gets anomaly flag for a specific location
 */
export function getAnomalyFlag(
  location: LocationData,
  allLocations: LocationData[],
  config?: Partial<AnomalyDetectionConfig>
): AnomalyFlag | null {
  const flags = detectAnomalies(allLocations, config);
  return flags.find(flag => flag.location.location === location.location) || null;
}

